import { createRouter, createWebHistory, RouteLocationNormalized } from 'vue-router';
import { Theme, UserType } from '@/constants';
import { globals } from '@/globals';
import utils from '@/helpers/utils';
import i18n from '@/plugins/i18n';
import { useAccountStore } from '@/stores/account';
import { useActivityStore } from '@/stores/activity';
import { useAlertStore } from '@/stores/alert';
import { useAppStore } from '@/stores/app';
import { useAppAlertStore } from '@/stores/appAlert';
import { useAuditStore } from '@/stores/audit';
import { useBetStore } from '@/stores/bet';
import { useBetSettlementStore } from '@/stores/betSettlement';
import { useBookBalanceStore } from '@/stores/bookBalance';
import { useBookmakerStore } from '@/stores/bookmaker';
import { useBotStore } from '@/stores/bot';
import { useBotUsageStatsStore } from '@/stores/botUsageStats';
import { useCurrencyExchangeStore } from '@/stores/currencyExchange';
import { useCustomerPlStore } from '@/stores/customerPl';
import { useCustomLeagueStore } from '@/stores/customLeaguePage';
import { useCustomTeamStore } from '@/stores/customTeam';
import { useLeagueStore } from '@/stores/league';
import { useLeagueDailyCoverageStore } from '@/stores/leagueDailyCoverage';
import { useLiveStore } from '@/stores/live';
import { useLoggedAgentsAdminsStore } from '@/stores/loggedAgentsAdmins';
import { useMappingProblemStore } from '@/stores/mappingProblem';
import { useMenuStore } from '@/stores/menu';
import { useMonitoringStore } from '@/stores/monitoring';
import { useNotificationStore } from '@/stores/notification';
import { useOrderStore } from '@/stores/order';
import { usePageVisitHistoryStore } from '@/stores/pageVisitHistory';
import { usePlayerOnlineStore } from '@/stores/playerOnline';
import { usePregameStore } from '@/stores/pregame';
import { useProxyStore } from '@/stores/proxy';
import { useTeamStore } from '@/stores/team';
import { useToolUserStore } from '@/stores/toolUser';
import { useToolUserDetailStore } from '@/stores/toolUserDetail';
import { useToolUserDocumentStore } from '@/stores/toolUserDocument';
import { useTransferStore } from '@/stores/transfer';
import { useTurnoverEventStore } from '@/stores/turnoverEvent';
import { useTurnoverSelectionStore } from '@/stores/turnoverSelection';
import { useUserStore } from '@/stores/user';
import { useUserBalanceStore } from '@/stores/userBalance';
import { useWlBalanceStore } from '@/stores/wlBalance';
import { useWlTransactionStore } from '@/stores/wlTransaction';

const routes = [
    {
        path: '/login',
        name: 'login',
        component: () => import('@/pages/login/LoginPage.vue'),
    },
    {
        path: '/stats/turnover/selection',
        name: 'turnoverSelection',
        component: () => import('@/pages/stats/turnover/selection/SelectionPage.vue'),
        meta: {
            store: () => useTurnoverSelectionStore('turnoverSelection'),
            menu: 'turnover',
            roles: [UserType.Admin, UserType.Company],
        },
    },
    {
        path: '/stats/turnover/game',
        name: 'turnoverEvent',
        component: () => import('@/pages/stats/turnover/game/GamePage.vue'),
        meta: {
            store: () => useTurnoverEventStore('turnoverEvent'),
            menu: 'turnover',
            roles: [UserType.Admin, UserType.Company],
        },
    },
    {
        path: '/events/pregame',
        name: 'pregame',
        component: () => import('@/pages/events/pregame/PregamePage.vue'),
        meta: {
            store: () => usePregameStore('pregame'),
            menu: 'events',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/events/live',
        name: 'live',
        component: () => import('@/pages/events/live/LivePage.vue'),
        meta: {
            store: () => useLiveStore('live'),
            menu: 'events',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/bets',
        name: 'bets',
        component: () => import('@/pages/admin/bets/BetsPage.vue'),
        meta: {
            store: () => useBetStore('bet'),
            menu: 'admin',
            roles: [UserType.Admin, UserType.Company, UserType.Agent, UserType.AgentView, UserType.AgentPlatform],
        },
    },
    {
        path: '/admin/bet/settlement',
        name: 'betSettlement',
        component: () => import('@/pages/admin/betSettlement/BetSettlementPage.vue'),
        meta: {
            store: () => useBetSettlementStore('betSettlement'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/league-daily-coverage',
        name: 'leagueDailyCoverage',
        component: () => import('@/pages/admin/leagueDailyCoverage/LeagueDailyCoveragePage.vue'),
        meta: {
            store: () => useLeagueDailyCoverageStore('leagueDailyCoverage'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/bots',
        name: 'bots',
        component: () => import('@/pages/admin/bots/BotsPage.vue'),
        meta: {
            store: () => useBotStore('bots'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/bot-usage-stats',
        name: 'botUsageStats',
        component: () => import('@/pages/admin/botUsageStats/BotUsageStatsPage.vue'),
        meta: {
            store: () => useBotUsageStatsStore('botUsageStats'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/transfer',
        name: 'transfer',
        component: () => import('@/pages/admin/transfer/TransferPage.vue'),
        meta: {
            store: () => useTransferStore('transfer'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/activity',
        name: 'activity',
        component: () => import('@/pages/admin/activity/ActivityPage.vue'),
        meta: {
            store: () => useActivityStore('activity'),
            menu: 'admin',
            roles: [UserType.Admin, UserType.Company],
        },
    },
    {
        path: '/admin/orders',
        name: 'orders',
        component: () => import('@/pages/admin/orders/OrdersPage.vue'),
        meta: {
            store: () => useOrderStore('order'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/notifications',
        name: 'notifications',
        component: () => import('@/pages/admin/notifications/NotificationPage.vue'),
        meta: {
            store: () => useNotificationStore('notification'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/system/audit',
        name: 'audit',
        component: () => import('@/pages/system/audit/AuditPage.vue'),
        meta: {
            store: () => useAuditStore('audit'),
            menu: 'system',
            roles: [UserType.Root],
        },
    },
    {
        path: '/system/currency-exchange',
        name: 'currencyExchange',
        component: () => import('@/pages/admin/currencyExchange/CurrencyExchangePage.vue'),
        meta: {
            store: () => useCurrencyExchangeStore('currencyExchange'),
            menu: 'system',
            roles: [UserType.Admin],
            title: 'exchangeRate',
        },
    },
    {
        path: '/system/bookmaker',
        name: 'bookmaker',
        component: () => import('@/pages/system/bookmaker/BookmakerPage.vue'),
        meta: {
            store: () => useBookmakerStore('bookmaker'),
            menu: 'system',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/system/monitoring',
        name: 'monitoring',
        component: () => import('@/pages/system/monitoring/MonitoringPage.vue'),
        meta: {
            store: () => useMonitoringStore(),
            menu: 'system',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/system/team',
        name: 'team',
        component: () => import('@/pages/system/team/TeamPage.vue'),
        meta: {
            store: () => useTeamStore('team'),
            menu: 'system',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/system/custom-team',
        name: 'customTeam',
        component: () => import('@/pages/system/customTeam/CustomTeamPage.vue'),
        meta: {
            store: () => useCustomTeamStore('customTeam'),
            menu: 'system',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/system/proxy',
        name: 'proxy',
        component: () => import('@/pages/admin/proxy/ProxyPage.vue'),
        meta: {
            store: () => useProxyStore('proxy'),
            menu: 'system',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/alerts',
        name: 'alerts',
        component: () => import('@/pages/system/alerts/AlertsPage.vue'),
        meta: {
            store: () => useAlertStore('alert'),
            menu: 'system',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/system/leagues',
        name: 'leagues',
        component: () => import('@/pages/system/leagues/LeaguesPage.vue'),
        meta: {
            store: () => useLeagueStore('leagues'),
            menu: 'system',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/system/custom-leagues',
        name: 'customLeagues',
        component: () => import('@/pages/system/customLeagues/CustomLeaguesPage.vue'),
        meta: {
            store: () => useCustomLeagueStore('customLeagues'),
            menu: 'system',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/app-alerts',
        name: 'appAlerts',
        component: () => import('@/pages/appAlerts/AppAlertsPage.vue'),
        meta: {
            store: () => useAppAlertStore('appAlert'),
            menu: 'system',
            roles: [UserType.Root],
        },
    },
    {
        path: '/system/logged-players',
        name: 'loggedPlayers',
        component: () => import('@/pages/system/loggedPlayers/LoggedPlayersPage.vue'),
        meta: {
            store: () => usePlayerOnlineStore('loggedPlayers'),
            menu: 'system',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/system/logged-agents-admins',
        name: 'loggedAgentsAdmins',
        component: () => import('@/pages/system/loggedAgentsAdmins/LoggedAgentsAdminsPage.vue'),
        meta: {
            store: () => useLoggedAgentsAdminsStore('loggedAgentsAdmins'),
            menu: 'system',
            roles: [UserType.Root],
        },
    },
    {
        path: '/system/page-visit-history',
        name: 'pageVisitHistory',
        component: () => import('@/pages/system/pageVisitHistory/PageVisitHistoryPage.vue'),
        meta: {
            store: () => usePageVisitHistoryStore('pageVisitHistory'),
            menu: 'system',
            roles: [UserType.Root],
        },
    },
    {
        path: '/accounting/book-balance',
        name: 'bookBalance',
        component: () => import('@/pages/accounting/bookBalance/BookBalancePage.vue'),
        meta: {
            store: () => useBookBalanceStore('bookBalance'),
            menu: 'accounting',
            roles: [UserType.Admin, UserType.Company],
        },
    },
    {
        path: '/accounting/user-balance',
        name: 'userBalance',
        component: () => import('@/pages/accounting/userBalance/UserBalancePage.vue'),
        meta: {
            store: () => useUserBalanceStore('userBalance'),
            menu: 'accounting',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/accounting/wl-balance',
        name: 'wlBalance',
        component: () => import('@/pages/accounting/wlBalance/WlBalancePage.vue'),
        meta: {
            store: () => useWlBalanceStore('wlBalance'),
            menu: 'accounting',
            roles: [UserType.Admin, UserType.Company],
        },
    },
    {
        path: '/accounting/customer-pl',
        name: 'customerPl',
        component: () => import('@/pages/accounting/customerPl/CustomerPlPage.vue'),
        meta: {
            store: () => useCustomerPlStore('customerPl'),
            menu: 'accounting',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/wlTransaction',
        name: 'wlTransaction',
        component: () => import('@/pages/admin/wlTransaction/WlTransactionPage.vue'),
        meta: {
            store: () => useWlTransactionStore('wlTransaction'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/accounts',
        name: 'accounts',
        component: () => import('@/pages/admin/accounts/AccountsPage.vue'),
        meta: {
            store: () => useAccountStore('account'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/tool-users',
        name: 'toolUsers',
        component: () => import('@/pages/admin/toolUsers/ToolUsersPage.vue'),
        meta: {
            store: () => useToolUserStore('toolUsers'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/tool-user-details',
        name: 'toolUserDetails',
        component: () => import('@/pages/admin/toolUserDetails/ToolUserDetailsPage.vue'),
        meta: {
            store: () => useToolUserDetailStore('toolUserDetails'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/tool-user-documents',
        name: 'toolUserDocuments',
        component: () => import('@/pages/admin/toolUserDocuments/ToolUserDocumentsPage.vue'),
        meta: {
            store: () => useToolUserDocumentStore('toolUserDocuments'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/admin/mappingProblem',
        name: 'mappingProblem',
        component: () => import('@/pages/admin/mappingProblem/MappingProblemPage.vue'),
        meta: {
            store: () => useMappingProblemStore('mappingProblem'),
            menu: 'admin',
            roles: [UserType.Admin],
        },
    },
    {
        path: '/:pathMatch(.*)*',
        redirect: '/admin/bets',
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

globals.router = router;

function hasQueryParams(route: RouteLocationNormalized): boolean {
    return !!Object.keys(route.query).length;
}

router.onError((err) => {
    console.error('Vue Router Error:', err);
});

router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    const userStore = useUserStore();
    const appStore = useAppStore();

    const exist = to.matched.length > 0;

    if (!exist) {
        return { name: 'bets' };
    }

    console.log('route to ' + to.path);
    if (to.name === 'login') {
        if (userStore.isLoggedIn()) {
            return { name: 'bets' };
        }

        //these pages always in Light theme
        if (appStore.currentTheme !== Theme.Light) appStore.currentTheme = Theme.Light;

        if (!hasQueryParams(to) && hasQueryParams(from)) {
            return { name: to.name, query: from.query };
        }

        return true;
    }

    if (!userStore.isLoggedIn()) {
        return { name: 'login' };
    }

    //roles check
    if (to.meta && to.meta.roles && !userStore.hasRole(to.meta.roles as UserType[])) {
        return { name: 'bets' };
    }

    if (localStorage.getItem('isDark') !== null) {
        appStore.currentTheme = localStorage.getItem('isDark') === 'true' ? Theme.Dark : Theme.Light;
    }

    utils.updateBreadcrumbs(to.name);
    return true;
});

router.afterEach((to, _from, failure) => {
    if (failure) return;

    const appName = import.meta.env.VITE_APP_NAME || 'Tradeball';
    const routeTitleKey = typeof to.meta.title === 'string' ? to.meta.title : typeof to.name === 'string' ? to.name : '';
    const translationKey = `menu.${routeTitleKey}`;
    const routeTitle = i18n.global.te(translationKey)
        ? i18n.global.t(translationKey)
        : routeTitleKey
              .split('-')
              .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
              .join(' ');

    document.title = routeTitle ? `${appName} | ${routeTitle}` : appName;

    if (typeof to.name === 'string') {
        useMenuStore().recordRecentMenuLink(to.name);
    }
});

// router.afterEach(() => {
//     if (vm.$socket && vm.$socket.readyState) {
//         let data = { pageUrl: window.location.toString() };
//
//         store.dispatch('sendObj', {
//             event: 'updateUserData',
//             data: data,
//         });
//
//         // vm.$socket.sendObj({
//         //     event: 'updateUserData',
//         //     data: data,
//         // });
//     }
// });

export default router;
